import React, { useState, useEffect, useRef } from 'react';
import { RESOURCES } from '../content/common';

const BASE_Z_INDEX = -2000;

function Sprite({ emoji, size = 40, style = {}, className = '', ...props }) {
  return (
    <div
      className={className}
      style={{
        fontSize: `${size}px`,
        lineHeight: 1,
        height: `${size}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        pointerEvents: 'none',
        ...style
      }}
      {...props}
    >
      {emoji}
    </div>
  );
}

function Shadow({ width = 20, height = 6 }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '100%',
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: 'rgba(0,0,0,0.2)',
        borderRadius: '50%',
        transform: 'translate(-50%, -50%) scale(1, 0.5)',
        filter: 'blur(2px)',
        pointerEvents: 'none',
      }}
    />
  );
}

function ChargeBar({ current, max }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: 0,
        transform: 'translate(-50%, -24px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '40px',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          color: '#4CAF50',
          fontSize: '12px',
          marginBottom: '2px',
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        {current}/{max}
      </div>
      <div
        style={{
          width: '100%',
          height: '8px',
          backgroundColor: 'rgba(255,255,255,0.8)',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
        }}
      >
        <div
          style={{
            width: `${(current / max) * 100}%`,
            height: '100%',
            backgroundColor: '#4CAF50',
            transition: 'width 0.2s ease-out'
          }}
        />
      </div>
    </div>
  );
}

function RechargeTimer({ secondsRemaining }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: 0,
        transform: 'translate(-50%, -24px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '40px',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          color: '#f59e0b',
          fontSize: '12px',
          marginBottom: '2px',
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        {secondsRemaining}s
      </div>
      <div
        style={{
          width: '100%',
          height: '8px',
          backgroundColor: 'rgba(255,255,255,0.8)',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
        }}
      >
        <div
          style={{
            width: `${(secondsRemaining / 60) * 100}%`,
            height: '100%',
            backgroundColor: '#f59e0b',
            transition: 'width 1s linear'
          }}
        />
      </div>
    </div>
  );
}

function FrozenOverlay() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(135deg, rgba(255,255,255,0.5), rgba(173,216,230,0.7))',
        borderRadius: '10px',
        pointerEvents: 'none',
        zIndex: 1,
        backdropFilter: 'blur(1px)',
        border: '2px solid rgba(255,255,255,0.8)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '5px',
          right: '5px',
          fontSize: '14px',
        }}
      >
        ❄️
      </div>
    </div>
  );
}

function GameStage({ game }) {
  const [tick, setTick] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      game.update(10);
      setTick(t => t + 1);
    }, 10);
    return () => clearInterval(interval);
  }, [game]);

  const getRelativeCoordinates = (clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const handlePointerDown = (e, position) => {
    const item = game.positions[position];

    if (item?.type === 'generator') {
      game.selectItem(position);
      return;
    }

    const { x, y } = getRelativeCoordinates(e.clientX, e.clientY);

    e.target.setPointerCapture(e.pointerId);

    game.startDrag(position, x, y);
  };

  const handlePointerMove = (e) => {
    if (!game.draggedItem) return;

    const { x, y } = getRelativeCoordinates(e.clientX, e.clientY);
    game.updateDragPosition(x, y);
  };

  const handlePointerUp = (e) => {
    if (!game.draggedItem) return;

    const { x, y } = getRelativeCoordinates(e.clientX, e.clientY);
    game.endDrag(x, y);
  };

  return (
    <div
      ref={containerRef}
      className="h-full w-full relative"
      style={{
        transform: 'translateZ(0)',
        userSelect: 'none',
        touchAction: 'none'
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div
        className="h-full w-full relative bg-gradient-to-b from-amber-50 to-orange-50"
        style={{ zIndex: BASE_Z_INDEX }}
      />

      {game.gridCoords.map((coord, i) => (
        <div
          key={`grid-${i}`}
          style={{
            position: 'absolute',
            left: coord.x,
            top: coord.y,
            width: '54px',
            height: '54px',
            background: 'linear-gradient(145deg, rgba(255,255,255,0.4), rgba(255,255,255,0.1))',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '12px',
            transform: 'translate(-50%, -50%)',
            zIndex: Math.floor(coord.y) + BASE_Z_INDEX,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05), inset 0 1px 2px rgba(255,255,255,0.2)',
            backdropFilter: 'blur(2px)',
          }}
          onClick={() => game.selectItem(i)}
        />
      ))}

      {Object.entries(game.positions).map(([position, item]) => {
        if (!item) return null;
        
        // Check if generator is recharging
        const isRecharging = item.type === 'generator' && item.isRecharging;
        const remainingRechargeTime = isRecharging ? game.getRemainingRechargeTime(item) : 0;
        
        // Determine cursor based on item type and state
        let cursor = 'grab';
        if (item.type === 'generator') {
          cursor = isRecharging ? 'not-allowed' : 'pointer';
        } else if (item.frozen) {
          cursor = 'not-allowed';
        }
        
        return (
          <div
            key={`item-${item.uid}`}
            style={{
              position: 'absolute',
              left: item.x,
              top: item.y,
              transform: 'translate(-50%, -50%)',
              zIndex: Math.floor(item.y) + BASE_Z_INDEX + 1,
              cursor: cursor,
              backgroundColor:
                game.selectedItem === item ? 'rgba(255,217,102,0.3)' : 'transparent',
              borderRadius: '10px',
              padding: '5px',
              opacity: isRecharging ? 0.5 : (item.type === 'generator' && item.isClicked ? 0.7 : 1),
              transition: 'opacity 0.1s',
              userSelect: 'none',
              boxShadow:
                game.selectedItem === item ? '0 0 12px rgba(255,217,102,0.5)' : 'none',
              pointerEvents: 'auto',
            }}
            onPointerDown={(e) => handlePointerDown(e, parseInt(position))}
          >
            <Shadow key={`shadow-${item.uid}`} width={30} height={8} />
            <Sprite 
              key={`sprite-${item.uid}`} 
              emoji={item.emoji} 
              size={40} 
              style={{
                filter: isRecharging ? 'grayscale(80%)' : 'none'
              }}
            />
            {item.frozen && <FrozenOverlay />}
            {item.type === 'generator' && (
              isRecharging ? (
                <RechargeTimer key={`recharge-${item.uid}`} secondsRemaining={remainingRechargeTime} />
              ) : item.infinite ? (
                <div key={`energy-${item.uid}`} style={{ position: 'absolute', left: '50%', top: 0, transform: 'translate(-50%, -24px)', color: '#ef4444', fontSize: '12px', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>-{item.energyCost} <img src={RESOURCES.energy.icon} alt="Energy" width={12} height={12} style={{display: 'inline'}} /></div>
              ) : (
                <ChargeBar key={`charge-${item.uid}`} current={item.charges} max={item.maxCharges} />
              )
            )}
          </div>
        );
      })}

      {game.draggedItem && (
        <div
          key={`dragged-${game.draggedItem.uid}`}
          style={{
            position: 'absolute',
            left: game.draggedItem.x,
            top: game.draggedItem.y,
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            cursor: 'grabbing',
            userSelect: 'none',
          }}
        >
          <Shadow key={`dragged-shadow-${game.draggedItem.uid}`} width={30} height={8} />
          <Sprite key={`dragged-sprite-${game.draggedItem.uid}`} emoji={game.draggedItem.emoji} size={40} />
        </div>
      )}
    </div>
  );
}

export default GameStage;
